import { Alert, Button, Drawer, Form, Input, Space, Radio, Select } from 'antd';
import {
  DrivingCompanyDocument,
  DrivingCompanyQuery,
  DrivingCompanyQueryVariables,
  GetAllDriversDocument,
  GetAllDriversQuery,
  GetAllDriversQueryVariables,
  IsEmailUsedForAuthDocument,
  IsEmailUsedForAuthQuery,
  IsEmailUsedForAuthQueryVariables,
  IsPhoneUsedForAuthenticationDocument,
  IsPhoneUsedForAuthenticationQuery,
  IsPhoneUsedForAuthenticationQueryVariables,
  useCreateDriverMutation,
  useGetAllDriversQuery,
  useGetDriverByEmailLazyQuery,
  useGetDriverByEmailQuery,
  useGetDriverQuery,
  useGetUserByEmailLazyQuery,
  useIsEmailUsedForAuthLazyQuery,
  useIsEmailUsedForAuthQuery,
  useUpdateDriverMutation,
} from '../../../../graphql/datamodel.gen';
import { getClient } from '../../../../apollo';
import { ALL_DIAL_CODES } from '../../../../constants/countrycodes';
import React, { useCallback } from 'react';
import { validatePhone } from '../../../../utils/phone';
import { QueryOptions } from '@apollo/client';

type AssignDriverDrawerProps = {
  visible: boolean;
  onClose: (driverId?: string) => void;
  drivingCompanyId: string;
  driverId?: string;
};

const UpsertDriverDrawer: React.FC<AssignDriverDrawerProps> = (
  props: AssignDriverDrawerProps,
) => {
  const { visible, onClose, drivingCompanyId, driverId } = props;
  const [createUserForm] = Form.useForm();

  const createDriverMutation = useCreateDriverMutation({
    client: getClient(),
  });

  const updateDriverMutation = useUpdateDriverMutation({
    client: getClient(),
  });

  const driverQuery = useGetDriverQuery({
    skip: !driverId,
    variables: {
      id: driverId,
    },
    fetchPolicy: 'cache-and-network',
  });

  const driver = driverQuery?.data?.driver;

  const onSubmit = (values) => {
    const [createDriver] = createDriverMutation;
    const [updateDriver] = updateDriverMutation;

    if (driver) {
      updateDriver({
        variables: {
          id: driver.id,
          name: values.name,
          email: values.email,
          phone: values.phone,
          loginMethod: values.loginMethod,
        },
        onCompleted: () => {
          onClose();
        },
      });
    } else {
      createDriver({
        variables: {
          email: values.email,
          name: values.name,
          type: 'collaborator',
          ...(values.loginMethod === 'PHONE' && {
            phone: `${values.prefix}${values.phone}`,
          }),
          ...(values.loginMethod === 'EMAIL' && {
            password: values.password,
          }),
          loginMethod: values.loginMethod,
          status: 'active',
          drivingCompanyId,
        },
        onCompleted: () => {
          onClose();
        },
        refetchQueries: [
          {
            query: DrivingCompanyDocument,
            variables: {
              id: drivingCompanyId,
              fetchDrivers: true,
            },
            fetchPolicy: 'network-only',
          } as QueryOptions<DrivingCompanyQueryVariables, DrivingCompanyQuery>,
        ],
        update: (cache, result) => {
          cache.updateQuery<GetAllDriversQuery, GetAllDriversQueryVariables>(
            {
              query: GetAllDriversDocument,
              variables: {
                fetchDrivingCompany: true,
              },
            },
            (data) => {
              return {
                drivers: [...data.drivers, result.data.createDriver],
              };
            },
          );
        },
      });
    }
  };

  const prefixSelector = (
    <Form.Item name="prefix" noStyle initialValue={'+34'}>
      <Select style={{ width: 70 }}>
        {ALL_DIAL_CODES.map((dialCode) => (
          <Select.Option key={dialCode} value={dialCode}>
            {dialCode}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  );

  const validateEmailCallback = React.useCallback((_, value) => {
    if (!value) {
      return Promise.resolve();
    }

    return getClient()
      .query<IsEmailUsedForAuthQuery, IsEmailUsedForAuthQueryVariables>({
        query: IsEmailUsedForAuthDocument,
        variables: {
          email: value,
        },
        fetchPolicy: 'network-only',
      })
      .then((result) => {
        if (!result.data?.isEmailUsedForAuthentication) {
          return Promise.resolve();
        } else if (value !== driver?.email) {
          return Promise.reject(new Error('Este email ya se está usando'));
        }
      });
  }, []);

  const validatePhoneCallback = React.useCallback((value) => {
    if (!value) {
      return Promise.resolve();
    }

    return getClient()
      .query<
        IsPhoneUsedForAuthenticationQuery,
        IsPhoneUsedForAuthenticationQueryVariables
      >({
        query: IsPhoneUsedForAuthenticationDocument,
        variables: {
          phone: value,
        },
        fetchPolicy: 'network-only',
      })
      .then((result) => {
        if (!result.data?.isPhoneUsedForAuthentication) {
          return Promise.resolve();
        } else {
          return Promise.reject(new Error('Este teléfono ya se está usando'));
        }
      });
  }, []);

  return (
    <Drawer
      destroyOnClose
      maskClosable={false}
      forceRender={false}
      onClose={() => onClose()}
      visible={visible}
      title="Crear conductor colaborador y asignar a empresa"
      {...(driver && {
        title: 'Gestionar conductor',
      })}
    >
      <Form
        form={createUserForm}
        name="create-membership-form"
        autoComplete="off"
        layout="vertical"
        onFinish={onSubmit}
        preserve={false}
        key={driver?.id}
      >
        <Form.Item
          label={'Nombre y apellidos'}
          name={'name'}
          initialValue={driver?.name}
          rules={[
            {
              required: true,
              message: 'Por favor indica nombre y apellidos',
            },
          ]}
        >
          <Input
            type={'text'}
            minLength={2}
            required={true}
            placeholder={'Ej: Satoshi Nakamoto'}
            autoCapitalize={'words'}
            autoFocus={true}
            autoComplete={'off'}
          />
        </Form.Item>
        <Form.Item
          label={'Correo electrónico'}
          required={true}
          requiredMark={true}
          name={'email'}
          initialValue={driver?.email}
          rules={[
            {
              required: true,
              type: 'email',
              message: 'Por favor introduce un correo electrónico válido',
            },
            {
              validator: validateEmailCallback,
              type: 'email',
            },
          ]}
        >
          <Input
            placeholder={'ejemplo@email.com'}
            type={'email'}
            required
            autoComplete={'off'}
          />
        </Form.Item>
        <Form.Item
          rules={[
            {
              required: true,
              message: 'Por favor selecciona un método de inicio de sesión',
            },
          ]}
          label={'Método de inicio de sesión'}
          required
          requiredMark
          name={'loginMethod'}
          initialValue={
            driver && !!driver?.loginMethod ? driver.loginMethod : 'PHONE'
          }
          {...(driver && {
            validateStatus: 'warning',
            help: 'El método de inicio de sesión no se puede modificar una vez dado de alta el conductor',
            style: {
              pointerEvents: 'none',
            },
          })}
        >
          <Radio.Group buttonStyle={'solid'}>
            <Radio.Button value={'PHONE'}>Teléfono móvil</Radio.Button>
            <Radio.Button value={'EMAIL'}>Correo electrónico</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item shouldUpdate>
          {({ getFieldValue }) => {
            if (driver) {
              return null;
            }

            if (getFieldValue('loginMethod') === 'EMAIL') {
              return (
                <Form.Item
                  label={'Contraseña'}
                  required={true}
                  requiredMark={true}
                  name={'password'}
                  rules={[
                    {
                      required: true,
                      message: 'Por favor, introduce una contraseña',
                    },
                  ]}
                >
                  <Input.Password
                    visibilityToggle
                    required
                    autoComplete={'off'}
                  />
                </Form.Item>
              );
            }

            if (getFieldValue('loginMethod') === 'PHONE') {
              return (
                <Form.Item
                  label={'Teléfono móvil'}
                  required={true}
                  requiredMark={true}
                  name={'phone'}
                  initialValue={driver?.phone}
                  rules={[
                    {
                      required: true,
                      message:
                        'Por favor, introduce un número de teléfono móvil',
                    },
                    {
                      validator: (_, value) => {
                        const invalidMessage = validatePhone(value, {
                          isRequired: {
                            enabled: false,
                          },
                        });
                        if (invalidMessage) {
                          return Promise.reject(new Error(invalidMessage));
                        }

                        return Promise.resolve();
                      },
                    },
                    ({ getFieldValue }) => ({
                      validator: (_, value) =>
                        validatePhoneCallback(
                          `${getFieldValue('prefix')}${value}`,
                        ),
                    }),
                  ]}
                >
                  <Input
                    addonBefore={prefixSelector}
                    type={'tel'}
                    required={true}
                    autoComplete={'off'}
                  />
                </Form.Item>
              );
            }
          }}
        </Form.Item>
        <Form.Item>
          <Button
            loading={
              updateDriverMutation[1].loading || createDriverMutation[1].loading
            }
            block
            type="primary"
            htmlType="submit"
          >
            {driver ? 'Guardar cambios' : 'Crear y asignar a empresa'}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default UpsertDriverDrawer;
